// "Around Tables" as an authored, live tour (Help pane live tours): each
// step does something on the real page through the page's own tools (open a
// folder, open a table, show the chart, open Edit Spec, add a variable to the
// rows and move it to the top, run the spec), then the help route explains
// it. The steps name the job's own folder, table and variable through picks
// (help/tourPicks), so the tour runs on any job.
//
// Quick Filter is outlined only: filtering is a question the person asks,
// and the pane builds the filter with them (Adam, 14 Oct).
//
// The edit is a preview and the tour never saves it: it ends on Tables with
// Discard outlined, and the person keeps or drops the change themselves.

import { FEATURE_SECTIONS, canRead, featureAudience } from '../features'
import type { HelpReader } from '../features'
import type { TourRun, TourStep } from '../tourRuntime'

export const TABLES_TOUR_ID = 'section:tables'

/** Every step of the tour, before it is filtered to what the reader may read. */
export const TABLES_TOUR_STEPS: TourStep[] = [
  {
    page: 'tables',
    feature: 'tables/table-of-contents',
    action: { tool: 'expand_toc_folder', args: { folder: { $pick: 'first_folder' } }, label: 'Open the first folder of shared tables' },
  },
  {
    page: 'tables',
    feature: 'tables/reading-a-table',
    action: { tool: 'open_toc_table', args: { table: { $pick: 'first_table_in_picked_folder' } }, label: 'Open the first table in it' },
  },
  {
    page: 'tables',
    feature: 'tables/quick-filter',
    control: 'Quick Filter',
    say: 'Ask me to filter this table and I will build the filter with you.',
  },
  {
    page: 'tables',
    feature: 'tables/chart-view',
    action: { tool: 'show_chart_view', label: 'Show it as a chart' },
  },
  {
    page: 'tables',
    feature: 'tables/quick-settings',
    action: { tool: 'show_table_view', label: 'Show it as a table again' },
    control: 'Quick Settings',
  },
  {
    page: 'tables',
    feature: 'tables/edit-spec-and-new-table',
    action: { tool: 'open_edit_spec', label: 'Open Edit Spec for this table' },
  },
  {
    route: 'editspec',
    feature: 'tables/edit-spec-page',
    action: { tool: 'add_variable_to_axis', args: { variable: { $pick: 'first_unused_variable' }, axis: 'side' }, label: 'Add a variable to the rows' },
  },
  {
    route: 'editspec',
    feature: 'tables/edit-spec-page',
    action: { tool: 'move_axis_item', args: { axis: 'side', item: { $pick: 'last_added_axis_item' }, direction: 'top' }, label: 'Move it to the top' },
  },
  {
    route: 'editspec',
    feature: 'tables/edit-spec-page',
    action: { tool: 'run_spec', label: 'Run the spec and see the preview' },
    say: 'Run Spec shows the result on Tables as a preview. Nothing is saved.',
  },
  {
    route: 'tables',
    feature: 'tables/unsaved-changes',
    control: 'Discard',
    say: 'This is a preview. Discard puts the table back as it was; Save Table would keep it. I never save for you during a tour.',
  },
]

/** "Around Tables" for this reader: the steps whose feature they may read. */
export function tablesTour(reader: HelpReader): TourRun {
  const tables = FEATURE_SECTIONS.find((s) => s.key === 'tables')
  const readable = (key?: string) => {
    const feature = tables?.features.find((f) => `tables/${f.key}` === key)
    return !!tables && !!feature && canRead(reader, featureAudience(tables, feature))
  }
  return { id: TABLES_TOUR_ID, name: 'Around Tables', steps: TABLES_TOUR_STEPS.filter((s) => readable(s.feature)) }
}
